import { faBroom } from '@fortawesome/free-solid-svg-icons';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
} from '../../components/ui';
import { useMyWorkerJobsQuery } from '../../services/serviceService/__generated__/queries.generated';
import { jobItemLabels, jobStatusView, windowText } from '../ServiceGuestPage/serviceJob';
import { stayErrorMessage } from '../StayHostPage/stayErrors';

export const WORKER_JOBS_PATH = '/services/worker';
export const workerJobPath = (bookingId: string) => `${WORKER_JOBS_PATH}/jobs/${bookingId}`;

/**
 * The jobs of the worker listings this login works (spec 019 W19). Without a
 * date: from yesterday on. The app is the main tool; the web is the backup.
 */
export default function WorkerJobsPage() {
  const navigate = useNavigate();
  const [date, setDate] = useState('');
  const query = useMyWorkerJobsQuery({
    variables: { date: date || null },
    fetchPolicy: 'cache-and-network',
  });
  const jobs = query.data?.myWorkerJobs ?? [];
  return (
    <PageContainer width="lg" canvas data-testid="worker-jobs">
      <PageHeader
        eyebrow="Worker"
        title="Your jobs"
        description="Jobs you do. The address and door code show 2 hours before each window."
      />
      <Stack $gap={6}>
        <InlineRow>
          <Field label="Day" htmlFor="worker-jobs-date">
            <Input
              id="worker-jobs-date"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </Field>
          {date && (
            <Button
              type="button"
              theme="text"
              size="sm"
              text="All coming jobs"
              onClick={() => setDate('')}
            />
          )}
        </InlineRow>
        {query.loading && !query.data ? (
          <LoadingBlock label="Loading your jobs" />
        ) : query.error && !query.data ? (
          <Alert tone="error" title="Could not load your jobs">
            {stayErrorMessage(query.error)}
          </Alert>
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={faBroom}
            title={
              date ? `No jobs on ${DateTime.fromISO(date).toFormat('LLL d')}` : 'No coming jobs'
            }
          />
        ) : (
          jobs.map(job => {
            const info = job.serviceJob;
            const status = jobStatusView(job);
            return (
              <Card
                key={job.id}
                $interactive
                onClick={() => navigate(workerJobPath(job.id))}
                style={{ cursor: 'pointer' }}
                data-testid={`worker-job-${job.id}`}
              >
                <CardBody>
                  <InlineRow style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div>
                      <Strong>{jobItemLabels(info?.items).join(' + ') || 'Job'}</Strong>
                      {info && (
                        <div>
                          <Muted $size="sm">
                            {windowText(info.windowStartAt, info.windowEndAt, info.timezone)} ·{' '}
                            {info.site?.city} {info.site?.zip}
                          </Muted>
                        </div>
                      )}
                    </div>
                    <Badge tone={status.tone}>{status.label}</Badge>
                  </InlineRow>
                </CardBody>
              </Card>
            );
          })
        )}
      </Stack>
    </PageContainer>
  );
}
